import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User } from 'services/auth/auth.types';
import { useCreateUserMutation, useGetUserMutation, useUpdateUserMutation } from 'services/user/user.endpoint';
import { CreateUserPayload, UpdateUserPayload } from 'services/user/user.types';
import { useApiToast } from 'components/Toaster';
export const useUserFormVM = () => {
    const { userId } = useParams();
    const [user, setUser] = useState<User | null>(null);
    const navigate = useNavigate();
    const { showApiToast, showErrorToast } = useApiToast();
    const [
        getUser,
        { isLoading: isGetUserLoading, isError: isGetUserError, isSuccess: isGetUserSuccess, error: getUserError, data: getUserData }
    ] = useGetUserMutation();

    const [
        updateUser,
        {
            isLoading: isUpdateUserLoading,
            isError: isUpdateUserError,
            isSuccess: isUpdateUserSuccess,
            error: updateUserError,
            data: updateUserData
        }
    ] = useUpdateUserMutation();

    const [
        createUser,
        {
            isLoading: isCreateUserLoading,
            isError: isCreateUserError,
            isSuccess: isCreateUserSuccess,
            error: createUserError,
            data: createUserData
        }
    ] = useCreateUserMutation();

    const handleUpdateUser = async (data: UpdateUserPayload) => {
        const response = await updateUser(data);
        if (response.data) {
            navigate('/app/admin/settings');
        }
    };

    const handleCreateUser = async (data: CreateUserPayload) => {
        const response = await createUser(data);
        if (response.data) {
            navigate('/app/admin/settings');
        }
    };

    useEffect(() => {
        getUser(userId);
    }, []);

    useEffect(() => {
        if (isUpdateUserLoading) return;
        if (isUpdateUserError && updateUserError) {
            showErrorToast(updateUserError, {
                message: `Erreur lors de la mise à jour de l'utilisateur "${user?.lastname} ${user?.firstname}"`
            });
            return;
        }
        if (isUpdateUserSuccess && updateUserData) {
            showApiToast(updateUserData, {
                successMessage: `Utilisateur "${user?.lastname} ${user?.firstname}" mis à jour avec succès`,
                errorMessage: `Erreur lors de la mise à jour de l'utilisateur "${user?.lastname} ${user?.firstname}"`
            });
            return;
        }
    }, [isUpdateUserLoading, isUpdateUserError, isUpdateUserSuccess, updateUserError, updateUserData]);

    useEffect(() => {
        if (isGetUserLoading) return;
        if (isGetUserError && getUserError) {
            showErrorToast(getUserError, {
                message: `Erreur lors de la récupération de l'utilisateur "${user?.lastname} ${user?.firstname}"`
            });
            return;
        }
        if (isGetUserSuccess && getUserData) {
            setUser(getUserData.data);
            return;
        }
    }, [isGetUserLoading, isGetUserError, isGetUserSuccess, getUserError, getUserData]);

    useEffect(() => {
        if (isCreateUserLoading) return;
        if (isCreateUserError && createUserError) {
            showErrorToast(createUserError, {
                message: `Erreur lors de la création de l'utilisateur "${user?.lastname} ${user?.firstname}"`
            });
            return;
        }
        if (isCreateUserSuccess && createUserData) {
            showApiToast(createUserData, {
                successMessage: `Utilisateur "${user?.lastname} ${user?.firstname}" créé avec succès`,
                errorMessage: `Erreur lors de la création de l'utilisateur "${user?.lastname} ${user?.firstname}"`
            });
            return;
        }
    }, [isCreateUserLoading, isCreateUserError, isCreateUserSuccess, createUserError, createUserData]);

    return {
        user,
        handleUpdateUser,
        handleCreateUser
    };
};
